import { z } from "zod";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface UnlockRedeemPayload {
  unlockedSlugs: string[];
  token: string;
  expiresInSeconds: number;
}

interface UnlockState {
  unlockedSlugs: string[];
  token: string | null;
  tokenExpiresAt: number | null;
  isUnlocked: (slug: string) => boolean;
  applyRedeem: (payload: UnlockRedeemPayload) => void;
  clear: () => void;
}

const persistedSchema = z.object({
  unlockedSlugs: z.array(z.string().min(1)),
  token: z.string().min(1).nullable(),
  tokenExpiresAt: z.number().nullable(),
});

function readTokenIfFresh(
  token: string | null,
  expiresAt: number | null,
): { token: string | null; tokenExpiresAt: number | null } {
  if (!token || !expiresAt) {
    return { token: null, tokenExpiresAt: null };
  }
  if (Date.now() >= expiresAt) {
    return { token: null, tokenExpiresAt: null };
  }
  return { token, tokenExpiresAt: expiresAt };
}

export const useUnlockStore = create<UnlockState>()(
  persist(
    (set, get) => ({
      unlockedSlugs: [],
      token: null,
      tokenExpiresAt: null,
      isUnlocked: (slug) => get().unlockedSlugs.includes(slug),
      applyRedeem: (payload) => {
        const expiresAt = Date.now() + payload.expiresInSeconds * 1000;
        set({
          unlockedSlugs: Array.from(new Set(payload.unlockedSlugs)).sort(),
          token: payload.token,
          tokenExpiresAt: expiresAt,
        });
      },
      clear: () =>
        set({ unlockedSlugs: [], token: null, tokenExpiresAt: null }),
    }),
    {
      name: "lp:unlock:v1",
      partialize: (state) => ({
        unlockedSlugs: state.unlockedSlugs,
        token: state.token,
        tokenExpiresAt: state.tokenExpiresAt,
      }),
      merge: (persisted, current) => {
        const parsed = persistedSchema.safeParse(persisted);
        if (!parsed.success) return current;
        const fresh = readTokenIfFresh(
          parsed.data.token,
          parsed.data.tokenExpiresAt,
        );
        return {
          ...current,
          unlockedSlugs: parsed.data.unlockedSlugs,
          token: fresh.token,
          tokenExpiresAt: fresh.tokenExpiresAt,
        };
      },
    },
  ),
);

export function getCurrentUnlockToken(): string | null {
  if (typeof window === "undefined") return null;
  const { token, tokenExpiresAt } = useUnlockStore.getState();
  const fresh = readTokenIfFresh(token, tokenExpiresAt);
  return fresh.token;
}
